import { NONRKPAY_INVOICE_STATUSES } from "@constants";
import { V3_INVOICE_TYPE_ID } from "@constants/index";

const isPendingClientReviewOrPayment = (statusId) =>
    [NONRKPAY_INVOICE_STATUSES.PENDING_CLIENT_REVIEW, NONRKPAY_INVOICE_STATUSES.PENDING_CLIENT_PAYMENT].includes(statusId);

const isInvoiceV2 = (invoiceVersion) => invoiceVersion === 2;
export const isInvoiceV3 = (invoiceVersion) => invoiceVersion === 3;

export const isCreatedOrPendingAgencyReview = (statusId) =>
    statusId === NONRKPAY_INVOICE_STATUSES.CREATED ||
    statusId === NONRKPAY_INVOICE_STATUSES.PENDING_AGENCY_REVIEW;

export const canPrint = (nonRKPayInvoiceDetailHook) => {
	const { invoiceState : { archived, invoiceVersion, electronicPaymentsMade } } = nonRKPayInvoiceDetailHook;

	return !archived && (isInvoiceV3(invoiceVersion) || !electronicPaymentsMade);
}

export const canDownload = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const { invoiceState : { invoiceId, invoiceVersion, isEstimate, finalized } } = nonRKPayInvoiceDetailHook;

	return (
		(invoiceId && isInvoiceV3(invoiceVersion) && !isSuperAdmin) ||
    	(isSuperAdmin && (isEstimate || finalized))
	);
}

export const showTimecardsNotes = (invoiceId, isTimeCardNotesModalEnabled) =>
    invoiceId && isTimeCardNotesModalEnabled;

export const showAttachments = (invoiceId, timecardAttachmentsInvoiceV4Enabled) =>
    invoiceId && timecardAttachmentsInvoiceV4Enabled;

const canUpdateV3Invoice = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const {
		invoiceState : { statusId, invoiceVersion, voided, archived, isEstimate, estimateFinalized, typeId}
	} = nonRKPayInvoiceDetailHook;

	// Officer invoices in Created status should not be editable
	if (!isEstimate && typeId === V3_INVOICE_TYPE_ID.OFFICER && statusId === NONRKPAY_INVOICE_STATUSES.CREATED) {
		return false;
	}

	return (
		isCreatedOrPendingAgencyReview(statusId) &&
		isInvoiceV3(invoiceVersion) &&
		!voided &&
		!archived &&
		!isSuperAdmin &&
		(
			!isEstimate ||
			isEstimate && !estimateFinalized
		)
	);
}

const canUpdateV2Invoice = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const {
		invoiceState : { invoiceVersion, voided, isEstimate, estimateFinalized, electronicPaymentsMade}
	} = nonRKPayInvoiceDetailHook;

	return isInvoiceV2(invoiceVersion) &&
	!voided &&
	!isSuperAdmin &&
	!electronicPaymentsMade &&
	(
		!isEstimate ||
		isEstimate && !estimateFinalized
	);
}

export const canUpdateInvoice = (nonRKPayInvoiceDetailHook, isSuperAdmin) =>
		canUpdateV3Invoice(nonRKPayInvoiceDetailHook, isSuperAdmin) ||
		canUpdateV2Invoice(nonRKPayInvoiceDetailHook, isSuperAdmin);

export const canApplyPayments = (invoiceVersion, isSuperAdmin) => isInvoiceV2(invoiceVersion) && !isSuperAdmin;


export const canInvoiceBeDeleted = (nonRKPayInvoiceDetailHook) => {
	const { invoiceState: { statusId, sentDateTimeUTC, totals, voided, typeId } } = nonRKPayInvoiceDetailHook;

	/** If is officer invoice it should not be deletable by any means */
	if(typeId === V3_INVOICE_TYPE_ID.OFFICER) {
		return false;
	}

	return isCreatedOrPendingAgencyReview(statusId) &&
		!sentDateTimeUTC &&
		!voided &&
		totals.appliedPayments === 0;
}

const canOfficerInvoiceBeVoided = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const { invoiceState : { statusId, voided } } = nonRKPayInvoiceDetailHook;

	return (
		statusId !== NONRKPAY_INVOICE_STATUSES.PAID && !voided && isSuperAdmin
	)
}

const canInvoiceBeVoided = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const {
		invoiceState : {
			statusId,
			voided,
			sentDateTimeUTC,
			invoiceVersion,
			totals,
			typeId
		},
		nonrkpayDetailPrepayEnhancementsEnabled,
		allowVoidNonrkpayInvoicesV2Enabled
	} = nonRKPayInvoiceDetailHook;

	/** Officer invoice condition should be
	 * handle separately to avoid confusion with other invoice types
	 */
	if(typeId === V3_INVOICE_TYPE_ID.OFFICER) {
		return canOfficerInvoiceBeVoided(nonRKPayInvoiceDetailHook, isSuperAdmin);
	}

	return (
		(
			(!isSuperAdmin && statusId !== NONRKPAY_INVOICE_STATUSES.PAID) ||
			(isSuperAdmin && isPendingClientReviewOrPayment(statusId))
		) &&
		!voided &&
		(
			nonrkpayDetailPrepayEnhancementsEnabled || sentDateTimeUTC
		)
		&&
		(allowVoidNonrkpayInvoicesV2Enabled || isInvoiceV3(invoiceVersion)) &&
		(
			(
				(
					statusId !== NONRKPAY_INVOICE_STATUSES.CREATED ||
					statusId !== NONRKPAY_INVOICE_STATUSES.PENDING_AGENCY_REVIEW
				) &&
				totals.balance > 0
			) ||
			totals.appliedPayments > 0 && isCreatedOrPendingAgencyReview(statusId)
		)
	);
}

const canEstimateBeVoided = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const {
		associatedInvoices,
		nonrkpayDetailPrepayEnhancementsEnabled,
		allowVoidNonrkpayInvoicesV2Enabled,
		invoiceState : {
			isEstimate,
			voided,
			sentDateTimeUTC,
			invoiceVersion,
			estimateFinalized
		}
	} = nonRKPayInvoiceDetailHook;

	return (
		isEstimate &&
		!isSuperAdmin &&
		!voided &&
		(
			nonrkpayDetailPrepayEnhancementsEnabled || sentDateTimeUTC
		) &&
		!estimateFinalized &&
		(allowVoidNonrkpayInvoicesV2Enabled || isInvoiceV3(invoiceVersion)) &&
		associatedInvoices &&
		associatedInvoices.length === 0
	);
}

export const isVoidDisabled = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const { invoiceState : { isEstimate } } = nonRKPayInvoiceDetailHook;

	return isEstimate
	? !canEstimateBeVoided(nonRKPayInvoiceDetailHook, isSuperAdmin)
	: !canInvoiceBeVoided(nonRKPayInvoiceDetailHook, isSuperAdmin);
}

export const canInvoiceRemindClient = (nonRKPayInvoiceDetailHook) => {
	const { invoiceState : { isEstimate, voided, statusId } } = nonRKPayInvoiceDetailHook;

	return !isEstimate && !voided && isPendingClientReviewOrPayment(statusId);
}

export const canApproveNonRKPayInvoice = (statusId, isSuperAdmin) =>
    statusId === NONRKPAY_INVOICE_STATUSES.PENDING_CLIENT_REVIEW && isSuperAdmin;

export const canMoveToPendingAgencyReview = (statusId, nonRKPayInvoiceDetailHook) =>
{
	const { invoiceState : { voided, totals } } = nonRKPayInvoiceDetailHook;

	return isPendingClientReviewOrPayment(statusId)
		&& totals.balance > 0
		&& !voided
}

export const canChangeToPendingClientReview = (statusId, isSuperAdmin) =>
    statusId === NONRKPAY_INVOICE_STATUSES.PENDING_CLIENT_PAYMENT && isSuperAdmin;

export const canInvoiceRecalculate = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const { invoiceState : { statusId, voided } } = nonRKPayInvoiceDetailHook;

	return statusId !== NONRKPAY_INVOICE_STATUSES.PAID &&
		!voided &&
		isSuperAdmin;
}

export const estimateAssociatedInvoicesArePaid = (associatedInvoices) => (
	associatedInvoices &&
	associatedInvoices.length > 0 &&
	associatedInvoices.every(invoice => invoice.StatusId === NONRKPAY_INVOICE_STATUSES.PAID)
)

const canEstimateBeArchived = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const { associatedInvoices, availableFunds, nonrkpayDetailPrepayEnhancementsEnabled, invoiceState : { archived, estimateFinalized, voided } } = nonRKPayInvoiceDetailHook;

	return (
		nonrkpayDetailPrepayEnhancementsEnabled &&
		!isSuperAdmin &&
		availableFunds === 0 &&
		!voided &&
		estimateFinalized &&
		(
			archived ||
			associatedInvoices?.length > 0
		)
	);
}

const canInvoiceBeArchived = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const { invoiceState : { totals, statusId, voided } } = nonRKPayInvoiceDetailHook

	return (
		!isSuperAdmin &&
		totals.balance === 0.0 &&
		statusId === NONRKPAY_INVOICE_STATUSES.PAID &&
		!voided
	);
}

export const canArchiveOrUnarchive = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const { invoiceState : { isEstimate } } = nonRKPayInvoiceDetailHook;

	return isEstimate
		? canEstimateBeArchived(nonRKPayInvoiceDetailHook, isSuperAdmin)
		: canInvoiceBeArchived(nonRKPayInvoiceDetailHook, isSuperAdmin);
}

export const isEstimateArchiveEnabled = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const { associatedInvoices } = nonRKPayInvoiceDetailHook;
	return canEstimateBeArchived(nonRKPayInvoiceDetailHook, isSuperAdmin) && estimateAssociatedInvoicesArePaid(associatedInvoices);
};

export const canAddInternalNotes = (nonRKPayInvoiceDetailHook) => {
	const { invoiceState : { invoiceId, invoiceVersion } } = nonRKPayInvoiceDetailHook;

	return invoiceId && isInvoiceV3(invoiceVersion);
}

export const existingInvoiceNoReviewNorPayment = (invoiceId, statusId) =>
    invoiceId && !isPendingClientReviewOrPayment(statusId);

export const canEstimateBeConverted = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const { invoiceState : { invoiceId, isEstimate, voided, lineItems, estimateFinalized }} = nonRKPayInvoiceDetailHook;

	return invoiceId && isEstimate && !isSuperAdmin && !voided && !estimateFinalized &&
    lineItems.filter(item => new Date(`${item.JobEndTime}Z`).getTime() > new Date().getTime()).length === 0;
}


export const canEstimateBeFinalized = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const { invoiceState : { estimateFinalized } } = nonRKPayInvoiceDetailHook;

	return !estimateFinalized && canEstimateBeConverted(nonRKPayInvoiceDetailHook, isSuperAdmin);
}
export const isInputDisabled = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const { invoiceState: { invoiceVersion, archived, finalized, voided, electronicPaymentsMade }} = nonRKPayInvoiceDetailHook;

	return (
		(isInvoiceV3(invoiceVersion) && (archived || finalized || voided)) ||
    	(!isInvoiceV3(invoiceVersion) && (archived || electronicPaymentsMade)) ||
    	isSuperAdmin
	);
}

const isInvoiceAppliedPaymentsAndVoided = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const { invoiceState : { totals, voided } } = nonRKPayInvoiceDetailHook;

	return totals.appliedPayments > 0 && voided && !isSuperAdmin;
}

const isInvoiceFinalizedAndNegativeBalance = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const { invoiceState : { totals, voided, finalized } } = nonRKPayInvoiceDetailHook;

	return finalized && !voided && totals.balance < 0 && !isSuperAdmin;
}

export const isInvoiceRefundable = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const { invoiceState : { invoiceVersion } } = nonRKPayInvoiceDetailHook;

	return (
		isInvoiceV3(invoiceVersion) &&
		(
			isInvoiceAppliedPaymentsAndVoided(nonRKPayInvoiceDetailHook, isSuperAdmin) ||
			isInvoiceFinalizedAndNegativeBalance(nonRKPayInvoiceDetailHook, isSuperAdmin)
		)
	);
}

export const isEstimateRefundable = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const {
		availableFunds,
		associatedInvoices,
		invoiceState : {
			isEstimate,
			estimateFinalized,
			voided,
			totals
		}
	} = nonRKPayInvoiceDetailHook;

	return (
		isEstimate &&
		!isSuperAdmin &&
		totals.appliedPayments > 0 && (
			voided
			||
			(
				estimateFinalized &&
				availableFunds > 0 &&
				associatedInvoices?.length > 0
			)
		)
	);
}

export const isEstimateRefundEnabled = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const {
		associatedInvoices,
		refundsInTransit,
		invoiceState:
		{
			voided,
		}
	} = nonRKPayInvoiceDetailHook;

	return isEstimateRefundable(nonRKPayInvoiceDetailHook, isSuperAdmin) &&
		(
			estimateAssociatedInvoicesArePaid(associatedInvoices) || voided
		) &&
		(!refundsInTransit || refundsInTransit?.length === 0);
};

export const canInvoiceBeCreatedFromEstimate = (nonRKPayInvoiceDetailHook, isSuperAdmin) => {
	const { associatedInvoices, invoiceState : { archived, isEstimate, voided, estimateFinalized, typeId } } = nonRKPayInvoiceDetailHook;

	return (
		isEstimate &&
		estimateFinalized &&
		!voided &&
		!archived &&
		!isSuperAdmin &&
		associatedInvoices &&
		associatedInvoices.length === 0 &&
		typeId !== V3_INVOICE_TYPE_ID.OFFICER
	)
}
